import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * <product-price> web component
 * - Swaps the rendered price when the selected variant changes.
 * - Uses event HTML when available; otherwise falls back to section fetch.
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Replace the current price container with a new one.
   * @param {HTMLElement} sourceRoot - Element containing the new price (HTML or fetched doc).
   */
  swapFromRoot(sourceRoot) {
    const newPrice = sourceRoot?.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (!newPrice || !currentPrice) return;
    if (currentPrice.innerHTML !== newPrice.innerHTML) currentPrice.replaceWith(newPrice);
  }

  /**
   * Fetch the section HTML for a given variant and swap the price.
   * @param {string} sectionId
   * @param {string} productUrl
   * @param {string|number} variantId
   */
  fetchAndSwap(sectionId, productUrl, variantId) {
    if (!sectionId || !productUrl || !variantId) return;
    const url = `${productUrl}?variant=${variantId}&section_id=${encodeURIComponent(sectionId)}`;
    fetch(url)
      .then(r => r.text())
      .then(txt => {
        const doc = new DOMParser().parseFromString(txt, 'text/html');
        this.swapFromRoot(doc);
      })
      .catch(console.error);
  }

  /**
   * Handle ThemeEvents.variantUpdate
   * @param {VariantUpdateEvent} event
   */
  updatePrice = (event) => {
    // Only react if this component belongs to the product that fired the event
    if (event?.detail?.data?.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (!currentPrice) return;

    // 1) Preferred: event provides ready-to-swap HTML
    const htmlRoot = event?.detail?.data?.html;
    if (htmlRoot instanceof HTMLElement || htmlRoot?.querySelector) {
      this.swapFromRoot(htmlRoot);
      return;
    }

    // 2) Fallback: fetch the section for the selected variant and swap
    const sectionId =
      this.closest('[data-section-id]')?.getAttribute('data-section-id') ||
      document.querySelector('[data-product-wrapper]')?.getAttribute('data-section-id');

    const productUrl =
      document.querySelector('[data-product-url]')?.getAttribute('data-product-url') ||
      this.closest('[data-product-wrapper]')?.getAttribute('data-product-url') ||
      window.location.pathname;

    const variantId =
      event?.detail?.data?.variant?.id ||
      document.querySelector('form[action*="/cart/add"] [name="id"]')?.value;

    this.fetchAndSwap(sectionId, productUrl, variantId);
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
